import { RedeAdsEnum } from '../enum/rede-ads.enum';

export interface RedeAdsCatalogEntry {
    readonly id: RedeAdsEnum;
    readonly slug: string;
    readonly iconPath: string;
    readonly label: string;
    readonly clickIdFields: readonly string[];
}

export interface RedeAdsAvailabilityContext {
    readonly hasSession: boolean;
    readonly isAdmin: boolean;
}

export interface RedeAdsDefinition extends RedeAdsCatalogEntry {
    readonly profileListPath: string;
    readonly isAvailable: (ctx: RedeAdsAvailabilityContext) => boolean;
}

export const REDE_ADS_CATALOG = [
    {
        id: RedeAdsEnum.GOOGLE_ADS,
        slug: 'google-ads',
        iconPath: 'assets/icone/rede_ads/google-ads.png',
        label: 'Google Ads',
        clickIdFields: ['gclid', 'gbraid', 'wbraid'],
    },
    {
        id: RedeAdsEnum.META_ADS,
        slug: 'meta-ads',
        iconPath: 'assets/icone/rede_ads/meta-ads.png',
        label: 'Meta Ads',
        clickIdFields: ['fbclid'],
    },
    {
        id: RedeAdsEnum.NEWSBREAK,
        slug: 'newsbreak-ads',
        iconPath: 'assets/icone/rede_ads/newsbreak-ads.png',
        label: 'News Break',
        clickIdFields: ['nb_clickid', 'nb_cid'],
    },
    {
        id: RedeAdsEnum.TABOOLA,
        slug: 'taboola-ads',
        iconPath: 'assets/icone/rede_ads/taboola-ads.svg',
        label: 'Taboola',
        clickIdFields: ['tbl_clickid', 'tblci'],
    },
    {
        id: RedeAdsEnum.TIK_TOK,
        slug: 'tiktok-ads',
        iconPath: 'assets/icone/rede_ads/tiktok-ads.png',
        label: 'TikTok',
        clickIdFields: ['ttclid', 'ttclickid'],
    },
    {
        id: RedeAdsEnum.MGID,
        slug: 'mgid-ads',
        iconPath: 'assets/icone/rede_ads/mgid-ads.svg',
        label: 'MGID',
        clickIdFields: ['mgid_clickid'],
    },
    {
        id: RedeAdsEnum.REVCONTENT,
        slug: 'revcontent-ads',
        iconPath: 'assets/icone/rede_ads/revcontent-ads.svg',
        label: 'RevContent',
        clickIdFields: ['rc_clickid'],
    },
] as const satisfies readonly RedeAdsCatalogEntry[];

export function findRedeAdsCatalogEntry(networkId: number | null | undefined): RedeAdsCatalogEntry | undefined {
    if (networkId == null) {
        return undefined;
    }
    return REDE_ADS_CATALOG.find((entry) => entry.id === networkId);
}

export function findRedeAdsByClickIdField(field: string | null | undefined): RedeAdsCatalogEntry | undefined {
    if (!field?.trim()) {
        return undefined;
    }
    const key = field.trim().toLowerCase();
    return REDE_ADS_CATALOG.find((entry) => entry.clickIdFields.some((item) => item === key));
}

export function redeAdsCatalogIconPath(networkId: number | null | undefined): string | null {
    return findRedeAdsCatalogEntry(networkId)?.iconPath ?? null;
}

function accountCenterEntry(
    id: RedeAdsEnum,
    profileListPath: string,
    isAvailable: (ctx: RedeAdsAvailabilityContext) => boolean,
): RedeAdsDefinition {
    const catalog = findRedeAdsCatalogEntry(id);
    if (!catalog) {
        throw new Error(`Missing rede-ads catalog entry for id ${id}`);
    }
    return { ...catalog, profileListPath, isAvailable };
}

export const REDE_ADS_DEFINITIONS = [
    accountCenterEntry(RedeAdsEnum.GOOGLE_ADS, '/v2/integrations/account-center/google-ads-profile-list', () => true),
    accountCenterEntry(
        RedeAdsEnum.META_ADS,
        '/v2/integrations/account-center/meta-ads-profile-list',
        (ctx) => ctx.hasSession,
    ),
    accountCenterEntry(
        RedeAdsEnum.NEWSBREAK,
        '/v2/integrations/account-center/newsbreak-ads-profile-list',
        (ctx) => ctx.hasSession,
    ),
    accountCenterEntry(
        RedeAdsEnum.TABOOLA,
        '/v2/integrations/account-center/taboola-ads-profile-list',
        (ctx) => ctx.hasSession,
    ),
] as const satisfies readonly RedeAdsDefinition[];

export const REDE_ADS_REGISTRY = REDE_ADS_DEFINITIONS;

export function findRedeAdsDefinition(networkId: RedeAdsEnum): RedeAdsDefinition | undefined {
    return REDE_ADS_DEFINITIONS.find((entry) => entry.id === networkId);
}
